import { Injectable, inject, type Renderer2, RendererFactory2, type RendererStyleFlags2 } from '@angular/core';

@Injectable()
export class CustomRendererService implements Renderer2 {
  private _rendererFactory = inject(RendererFactory2);

  private _renderer: Renderer2;

  constructor() {
    this._renderer = this._rendererFactory.createRenderer(null, null);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  get data(): { [key: string]: any } {
    return this._renderer.data;
  }

  destroy(): void {
    this._renderer.destroy();
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  destroyNode(node: any): void {
    if (this._renderer.destroyNode) {
      this._renderer.destroyNode(node);
    }
  }

  createElement(name: string, namespace?: string | null) {
    return this._renderer.createElement(name, namespace);
  }

  createComment(value: string) {
    this._renderer.createComment(value);
  }

  createText(value: string) {
    this._renderer.createText(value);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  appendChild(parent: any, newChild: any): void {
    this._renderer.appendChild(parent, newChild);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  insertBefore(parent: any, newChild: any, refChild: any): void {
    this._renderer.insertBefore(parent, newChild, refChild);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  removeChild(parent: any, oldChild: any, isHostElement?: boolean): void {
    this._renderer.removeChild(parent, oldChild, isHostElement);
  }

  /**
   * Custom implementation of `selectRootElement` to avoid throwing errors upon not finding element
   * @param selectorOrNode The DOM element.
   * @param preserveContent Whether the contents of the root element
   * should be preserved (default behavior), or cleared upon bootstrap.
   *
   * Use with `ViewEncapsulation.ShadowDom` to allow simple native
   * content projection via `<slot>` elements.
   *
   * @returns The root element or `undefined`.
   */

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  selectRootElement(selectorOrNode: any, preserveContent: boolean = true): any | undefined {
    try {
      const node = this._renderer.selectRootElement(selectorOrNode, preserveContent);
      return node;
    } catch {
      return undefined;
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  parentNode(node: any) {
    return this._renderer.parentNode(node);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  nextSibling(node: any) {
    return this._renderer.nextSibling(node);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  setAttribute(el: any, name: string, value: string, namespace?: string | null): void {
    this._renderer.setAttribute(el, name, value, namespace);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  removeAttribute(el: any, name: string, namespace?: string | null): void {
    this._renderer.removeAttribute(el, name, namespace);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  addClass(el: any, name: string): void {
    this._renderer.addClass(el, name);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  removeClass(el: any, name: string): void {
    this._renderer.removeClass(el, name);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  setStyle(el: any, style: string, value: any, flags?: RendererStyleFlags2): void {
    this._renderer.setStyle(el, style, value, flags);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  removeStyle(el: any, style: string, flags?: RendererStyleFlags2): void {
    this._renderer.removeStyle(el, style, flags);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  setProperty(el: any, name: string, value: any): void {
    this._renderer.setProperty(el, name, value);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  setValue(node: any, value: string): void {
    this._renderer.setValue(node, value);
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  listen(target: any, eventName: string, callback: (event: any) => boolean | void): () => void {
    return this._renderer.listen(target, eventName, callback);
  }
}
